import { useEffect, useState } from "react";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { IMAGES } from "@/lib/constants";
import { fetchGlobalNodes } from "@/lib/api";

const TYPE_LABELS = {
  headquarters: "Headquarters",
  regional_office: "Regional office",
  research_hub: "Research hub",
  pilot_project: "Pilot project",
};

export default function MapPage() {
  const [nodes, setNodes] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { fetchGlobalNodes().then(setNodes); }, []);

  return (
    <div data-testid="map-page">
      <PageHero
        testId="map-hero"
        eyebrow="Global presence"
        title="A discipline taking root in 32 countries."
        lead="Headquarters, regional offices, research hubs, pilot projects and partner institutions. Click a node to see what's happening on the ground."
      />

      <section className="container-x border-t border-border py-10">
        <div className="relative rounded-3xl overflow-hidden border border-border bg-[#04070e]">
          <img src={IMAGES.globalMap} alt="Global map" className="w-full h-auto opacity-70" />
          {nodes.map((n) => (
            <button
              key={n.id}
              data-testid={`map-node-${n.id}`}
              onClick={() => setActive(n)}
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
            >
              <span className="relative inline-flex h-3 w-3">
                <span className="absolute inset-0 rounded-full bg-[hsl(var(--accent))]/40 animate-ping" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[hsl(var(--accent))] ring-2 ring-black/30" />
              </span>
              <span className="absolute left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap text-[10px] font-mono-mini text-white/90 opacity-0 group-hover:opacity-100 transition-opacity">
                {n.city}
              </span>
            </button>
          ))}
          {active && (
            <div data-testid="map-detail" className="absolute bottom-4 right-4 max-w-xs rounded-xl border border-white/10 bg-black/70 backdrop-blur-xl p-5 text-white">
              <div className="font-mono-mini text-[hsl(var(--accent))]">{TYPE_LABELS[active.type]}</div>
              <div className="mt-2 font-display text-xl font-medium tracking-tight">{active.city}</div>
              <div className="text-xs text-white/70">{active.country}</div>
              <button onClick={() => setActive(null)} data-testid="map-detail-close" className="mt-4 text-xs underline-link">close</button>
            </div>
          )}
        </div>
      </section>

      <section className="container-x section-y">
        <SectionEyebrow>Nodes</SectionEyebrow>
        <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">Where the discipline is being built.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {nodes.map((n, i) => (
            <Reveal key={n.id} delay={i * 0.03}>
              <div data-testid={`node-card-${n.id}`} className="rounded-xl border border-border bg-card p-5 flex items-center justify-between hover-lift">
                <div>
                  <div className="font-display font-medium">{n.city}</div>
                  <div className="text-xs text-muted-foreground">{n.country}</div>
                </div>
                <span className="font-mono-mini text-[hsl(var(--accent))]">{TYPE_LABELS[n.type]}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
